import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function CertificateInvoicePreferencesFields({
  includeRut,
  onIncludeRutChange,
  rutDigits,
  onRutDigitsChange,
  rutLockedFromRegistration = false,
}: {
  includeRut: boolean;
  onIncludeRutChange: (value: boolean) => void;
  rutDigits: string;
  onRutDigitsChange: (digits: string) => void;
  /** When true (company account at signup), RUT is always on the invoice; no opt-out. */
  rutLockedFromRegistration?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  if (rutLockedFromRegistration) {
    const displayDigits = rutDigits.replace(/\D/g, "").slice(0, 12);
    return (
      <div className="wizard-summary-panel space-y-4 p-4">
        <h3 className="text-base font-semibold text-foreground">
          {t("requests.user.pricingInvoice.sectionTitle")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("requests.user.pricingInvoice.lockedCompanyInvoiceHint")}
        </p>
        <div className="space-y-2">
          <Label htmlFor="cert-invoice-rut-locked" className="text-sm font-medium text-muted-foreground">
            {t("requests.user.pricingInvoice.rutLabel")}
          </Label>
          <Input
            id="cert-invoice-rut-locked"
            readOnly
            value={displayDigits}
            className="max-w-xs bg-muted/50 transition-colors duration-150"
            tabIndex={-1}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-summary-panel space-y-4 p-4">
      <h3 className="text-base font-semibold text-foreground">
        {t("requests.user.pricingInvoice.sectionTitle")}
      </h3>
      <div className="flex flex-row items-start gap-3">
        <Checkbox
          id="cert-invoice-include-rut"
          checked={includeRut}
          onCheckedChange={(v) => onIncludeRutChange(v === true)}
          className="mt-0.5 transition-colors duration-150"
        />
        <Label
          htmlFor="cert-invoice-include-rut"
          className="cursor-pointer text-sm font-medium leading-snug text-foreground"
        >
          {t("requests.user.pricingInvoice.includeRutLabel")}
        </Label>
      </div>
      {includeRut ? (
        <div className="space-y-2 pl-0 sm:pl-7">
          <Label htmlFor="cert-invoice-rut" className="text-sm font-medium text-muted-foreground">
            {t("requests.user.pricingInvoice.rutLabel")}
          </Label>
          <Input
            id="cert-invoice-rut"
            value={rutDigits}
            onChange={(e) => {
              onRutDigitsChange(e.target.value.replace(/\D/g, "").slice(0, 12));
            }}
            inputMode="numeric"
            maxLength={12}
            placeholder={t("requests.user.pricingInvoice.rutPlaceholder")}
            className="max-w-xs transition-colors duration-150"
            autoComplete="off"
          />
          <p className="text-xs text-muted-foreground">{t("requests.user.pricingInvoice.rutHint")}</p>
        </div>
      ) : null}
    </div>
  );
}
